import React, { useState, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { apiUrl, parseApiResponse } from '../services/api';
import { setAuthSession, clearAuthSession, consumeAuthReturnPath, getAuthToken, isTokenExpired } from '../services/auth';
import BrandLockup from '../components/BrandLockup';
import AccountCreationModal from '../domains/identity/AccountCreationModal';
import GoogleSignInButton from '../domains/identity/GoogleSignInButton';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [googleSetup, setGoogleSetup] = useState(null);
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState('');
  const navigate = useNavigate();
  const toast = useRef(null);

  // Already authenticated — go straight to dashboard
  const token = getAuthToken();
  const invitationToken = new URLSearchParams(window.location.search).get('invitation');
  const authenticatedDestination = invitationToken
    ? `/dashboard?invitation=${encodeURIComponent(invitationToken)}`
    : '/dashboard';
  if (token && !isTokenExpired(token)) {
    return <Navigate to={authenticatedDestination} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Keep the API namespace explicit so a stale/misconfigured production
      // build cannot send this POST to the static nginx frontend.
      const endpoint = pendingGoogleCredential ? '/api/auth/google/link' : '/api/auth/login';
      const payload = pendingGoogleCredential
        ? { credential: pendingGoogleCredential, email, password }
        : { email, password };
      const res = await fetch(apiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await parseApiResponse(res);
      clearAuthSession(); // clear any stale session before setting the new one
      setAuthSession(data.token, data.email);
      setPendingGoogleCredential('');
      navigate(consumeAuthReturnPath() || authenticatedDestination, { replace: true });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: error?.name === 'ApiError' ? 'Sign In Failed' : 'Connection Error',
        detail: error?.message || 'Unable to connect to server',
        life: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const finishAuthentication = (data) => {
    clearAuthSession();
    setAuthSession(data.token, data.email);
    setGoogleSetup(null);
    setAccountModalOpen(false);
    navigate(consumeAuthReturnPath() || authenticatedDestination, { replace: true });
  };

  const handleGoogleCredential = async (credential) => {
    setLoading(true);
    try {
      const response = await fetch(apiUrl('/api/auth/google'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
      });
      const data = await parseApiResponse(response);
      if (data.onboarding_required) {
        setGoogleSetup({ setupToken: data.setup_token, profile: data.profile });
        setAccountModalOpen(true);
      } else {
        finishAuthentication(data);
      }
    } catch (error) {
      if (error?.data?.code === 'account_link_required') {
        setPendingGoogleCredential(credential);
        setEmail(error.data.email || '');
        toast.current?.show({
          severity: 'info',
          summary: 'Confirm Your Existing Account',
          detail: 'Enter your current GravviSoft password to link Google securely.',
          life: 7000,
        });
        setLoading(false);
        return;
      }
      toast.current?.show({
        severity: 'error',
        summary: 'Google Sign In Failed',
        detail: error?.message || 'Unable to sign in with Google',
        life: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-page login-page--split">
      <Toast ref={toast} position="top-right" />
      <section className="login-card login-card--signin" aria-label="Sign in">
        <div className="login-brand">
          <BrandLockup className="brand-lockup--auth" title="Sign In" />
        </div>

        <div className="login-external login-external--first">
          {process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID ? (
            <GoogleSignInButton
              clientId={process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID}
              onCredential={handleGoogleCredential}
              onUnavailable={() => toast.current?.show({ severity: 'error', summary: 'Google Unavailable', detail: 'Google sign-in could not load.', life: 4000 })}
            />
          ) : (
            <>
              <button className="google-sign-in-preview" type="button" disabled aria-label="Continue with Google — setup required">
                <span className="google-sign-in-preview__mark" aria-hidden="true">G</span>
                <span>Google</span>
              </button>
            </>
          )}
          <div className="login-external__divider"><span>or use email</span></div>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          {pendingGoogleCredential && (
            <div className="login-link-notice" role="status">
              Confirm your current password once to link Google to this account.
              <button type="button" onClick={() => setPendingGoogleCredential('')}>Cancel</button>
            </div>
          )}
          <div className="login-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus
            />
          </div>

          <div className="login-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>

          <button className="login-submit" type="submit" disabled={loading}>
            {loading ? 'Signing in…' : pendingGoogleCredential ? 'Link Google & Sign In' : 'Sign In'}
          </button>
        </form>

        {/* <p className="login-switch">
          Don't have an account? <Link to="/register">Create one</Link>
        </p> */}
      </section>

      <aside className="login-welcome" aria-label="Create a GravviSoft account">
        <div>
          <h2>Create Account</h2>
          <p>Enter your details and begin your journey with us.</p>
        </div>
        <button className="login-create-account" type="button" onClick={() => setAccountModalOpen(true)}>
          Create Account <i className="pi pi-arrow-right" aria-hidden="true" />
        </button>
      </aside>

      <AccountCreationModal
        open={accountModalOpen}
        googleSetup={googleSetup}
        onAuthenticated={finishAuthentication}
        onClose={() => { setAccountModalOpen(false); setGoogleSetup(null); }}
      />
    </main>
  );
};

export default Login;
